import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  AdminPanelSettings,
  AttachMoneyOutlined,
  CheckCircleOutline,
  ErrorOutline,
  GroupsOutlined,
  InsightsOutlined,
  PersonAddOutlined,
  SupportAgentOutlined,
  TokenOutlined,
  TrendingDownOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import moment from "moment";
import { GlobalContext } from "../../context/GlobalContext";
import PageHeader from "../../common/PageHeader";

const BRAND = "#6D28D9";
const GREEN = "#059669";
const AMBER = "#D97706";
const RED = "#DC2626";
const BLUE = "#2563EB";
const SLATE = "#64748B";
const CHART_COLORS = [BRAND, BLUE, GREEN, AMBER, RED, "#0891B2", "#DB2777", "#4F46E5"];

const TASK_LABELS = {
  inf_maker: "Influencer Create",
  inf_var_maker: "Influencer Variation",
  content_video: "Content Video",
  product_showcase: "Product Showcase",
  product_showcase_maker: "Product Showcase",
  video_studio_generation: "Video Studio",
  video_stitch: "Video Stitch",
  signup_trial: "Signup Trial",
  text_generation: "Text Generation",
  video_agent_generation: "Video Agent",
  viral_pipeline_generation: "Viral Pipeline",
};
const taskLabel = (t) => TASK_LABELS[t] || t || "—";

function fmtN(n, digits = 0) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function fmtMoney(n) {
  return fmtN(n, 2);
}

function fmtCompact(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return fmtN(v);
}

function shortDate(d) {
  return moment(d).format("D MMM");
}

function ChartCard({ title, subtitle, children, height = 280, action }) {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={800}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Stack>
      <Box sx={{ width: "100%", height, flex: 1, minHeight: height }}>{children}</Box>
    </Box>
  );
}

function KpiCard({ label, value, hint, tone = "neutral", icon: Icon, delta }) {
  const theme = useTheme();
  const toneMap = {
    positive: GREEN,
    negative: RED,
    brand: BRAND,
    amber: AMBER,
    blue: BLUE,
    neutral: theme.palette.text.primary,
  };
  const color = toneMap[tone] || toneMap.neutral;
  return (
    <Box
      sx={{
        p: 2.25,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, transparent 55%)`,
          pointerEvents: "none",
        }}
      />
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ position: "relative" }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(color, 0.12),
            color,
            flexShrink: 0,
          }}
        >
          {Icon ? <Icon fontSize="small" /> : <InsightsOutlined fontSize="small" />}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.15, color, letterSpacing: -0.4 }}>
            {value}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
            {delta != null ? (
              <Chip
                size="small"
                icon={Number(delta) >= 0 ? <TrendingUpOutlined /> : <TrendingDownOutlined />}
                label={`${Number(delta) >= 0 ? "+" : ""}${fmtN(delta, 1)}%`}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  bgcolor: alpha(Number(delta) >= 0 ? GREEN : RED, 0.12),
                  color: Number(delta) >= 0 ? GREEN : RED,
                  "& .MuiChip-icon": { color: "inherit", fontSize: 14 },
                }}
              />
            ) : null}
            {hint ? (
              <Typography variant="caption" color="text.disabled" noWrap>
                {hint}
              </Typography>
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

function chartTooltipStyle(theme) {
  return {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    fontSize: 12,
  };
}

const Dash = ({ lang }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await hitAxios({
          path: "/api/admin/get_dashboard",
          post: false,
          admin: true,
          showSnackbar: false,
        });
        if (cancelled) return;
        if (res?.data?.success && res?.data?.data != null) setData(res.data.data);
        else setLoadError(res?.data?.msg || "Failed to load dashboard");
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || "Failed to load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hitAxios]);

  const derived = useMemo(() => {
    if (!data) return null;
    const finance = data.finance || {};
    const stats = data.stats || {};
    const flow = Array.isArray(data.credit_flow_30d) ? data.credit_flow_30d : [];
    const revenue = Array.isArray(data.revenue_chart_30d) ? data.revenue_chart_30d : [];
    const growth = Array.isArray(data.user_growth_30d) ? data.user_growth_30d : data.user_growth || [];
    const tasks = Array.isArray(data.task_breakdown) ? data.task_breakdown : [];
    const statusMix = Array.isArray(data.ops_analytics?.status_mix_7d)
      ? data.ops_analytics.status_mix_7d
      : [];

    const spentLast7 = flow.slice(-7).reduce((s, r) => s + Number(r.credits_spent || 0), 0);
    const spentPrev7 = flow.slice(-14, -7).reduce((s, r) => s + Number(r.credits_spent || 0), 0);
    const spentDelta =
      spentPrev7 > 0 ? ((spentLast7 - spentPrev7) / spentPrev7) * 100 : spentLast7 > 0 ? 100 : 0;

    const revLast7 = revenue.slice(-7).reduce((s, r) => s + Number(r.revenue || 0), 0);
    const revPrev7 = revenue.slice(-14, -7).reduce((s, r) => s + Number(r.revenue || 0), 0);
    const revDelta = revPrev7 > 0 ? ((revLast7 - revPrev7) / revPrev7) * 100 : revLast7 > 0 ? 100 : 0;

    const usersLast7 = growth.slice(-7).reduce((s, r) => s + Number(r.count || 0), 0);
    const usersPrev7 = growth.slice(-14, -7).reduce((s, r) => s + Number(r.count || 0), 0);
    const usersDelta =
      usersPrev7 > 0 ? ((usersLast7 - usersPrev7) / usersPrev7) * 100 : usersLast7 > 0 ? 100 : 0;

    const taskPie = tasks.slice(0, 8).map((t) => ({
      name: taskLabel(t.task),
      value: Number(t.total_credits || 0),
      count: Number(t.count || 0),
    }));

    const planMix = [
      { name: "Paid", value: Number(stats.users?.paid || 0) },
      { name: "Free / Trial", value: Number(stats.users?.free || 0) },
    ].filter((x) => x.value > 0);

    const statusPie = statusMix.map((s) => ({
      name: String(s.status || "other"),
      value: Number(s.cnt || 0),
    }));

    return {
      finance,
      stats,
      flow,
      revenue,
      growth,
      tasks,
      taskPie,
      planMix,
      statusPie,
      spentDelta,
      revDelta,
      usersDelta,
      margin: finance.generation_margin || {},
      ops: data.ops_analytics?.summary || {},
    };
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || !derived) {
    return (
      <Box sx={{ textAlign: "center", py: 10 }}>
        <Typography color="text.secondary">
          {loadError || lang?.dashLoadError || "Failed to load dashboard"}
        </Typography>
      </Box>
    );
  }

  const { finance, stats, flow, revenue, growth, tasks, taskPie, planMix, statusPie, margin, ops } =
    derived;
  const tip = chartTooltipStyle(theme);
  const axisColor = theme.palette.text.disabled;
  const gridColor = alpha(theme.palette.divider, 0.9);

  return (
    <>
      <PageHeader
        icon={AdminPanelSettings}
        title={lang?.adminDashboard || "Executive Dashboard"}
        subtitle={`${moment().format("dddd, D MMMM YYYY")} · Revenue, credit burn, and generation economics`}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Finance KPI strip */}
        <Grid container spacing={1.75}>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <KpiCard
              icon={AttachMoneyOutlined}
              label="Revenue (MTD)"
              value={fmtMoney(finance.revenue_mtd)}
              hint={`${fmtMoney(finance.revenue_30d)} · last 30d`}
              tone="positive"
              delta={derived.revDelta}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <KpiCard
              icon={TokenOutlined}
              label="Credit Burn (7d)"
              value={fmtCompact(stats.credits_spent_7d)}
              hint={`${fmtCompact(stats.credits_spent_today)} today`}
              tone="brand"
              delta={derived.spentDelta}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <KpiCard
              icon={InsightsOutlined}
              label="Gen. Margin"
              value={`${fmtN(margin.margin_pct, 1)}%`}
              hint={`${fmtCompact(margin.profit_credits)} cr profit`}
              tone={Number(margin.margin_pct) >= 0 ? "positive" : "negative"}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <KpiCard
              icon={AccountBalanceWalletOutlined}
              label="Wallet Liability"
              value={fmtCompact(finance.wallet_liability)}
              hint={`${fmtN(finance.users_zero_credits)} users at 0`}
              tone="amber"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <KpiCard
              icon={GroupsOutlined}
              label="Paid Conversion"
              value={`${fmtN(finance.paid_user_pct, 1)}%`}
              hint={`${fmtN(stats.users?.paid)} / ${fmtN(stats.users?.total)} users`}
              tone="blue"
              delta={derived.usersDelta}
            />
          </Grid>
        </Grid>

        {/* Secondary ops KPIs */}
        <Grid container spacing={1.75}>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              icon={PersonAddOutlined}
              label="New Users (MTD)"
              value={fmtN(stats.users?.new_this_month)}
              hint={`${fmtN(stats.users?.new_today)} today`}
              tone="blue"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              label="Lifetime Revenue"
              value={fmtMoney(finance.revenue_all_time)}
              hint={`AOV ${fmtMoney(finance.avg_order_value)}`}
              tone="positive"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              label="Credits Spent"
              value={fmtCompact(stats.credits_spent)}
              hint="All-time burn"
              tone="brand"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              label="Refunds (30d)"
              value={fmtCompact(finance.credits_refunded_30d)}
              hint={`${fmtCompact(finance.credits_refunded_7d)} in 7d`}
              tone="negative"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              icon={SupportAgentOutlined}
              label="Open Support"
              value={fmtN(stats.support?.unanswered)}
              hint={`${fmtN(stats.support?.total)} total`}
              tone="amber"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <KpiCard
              label="Failures (7d)"
              value={fmtN(ops.failure_events_7d)}
              hint="Errors + refunds"
              tone="negative"
            />
          </Grid>
        </Grid>

        {/* Charts row 1 */}
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="Credit Flow (30 days)"
              subtitle="Successful generation spend vs refunds — primary cost signal"
              height={320}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={flow} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={BRAND} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={BRAND} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={gridColor} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={shortDate}
                    tick={{ fill: axisColor, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={28}
                  />
                  <YAxis
                    tick={{ fill: axisColor, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={fmtCompact}
                    width={48}
                  />
                  <Tooltip
                    contentStyle={tip}
                    labelFormatter={(l) => moment(l).format("ddd, D MMM YYYY")}
                    formatter={(v, name) => [fmtN(v), name === "credits_spent" ? "Spent" : "Refunded"]}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="credits_spent"
                    name="Spent"
                    stroke={BRAND}
                    fill="url(#spendFill)"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Bar
                    dataKey="credits_refunded"
                    name="Refunded"
                    fill={RED}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={14}
                    opacity={0.85}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <ChartCard
              title="Generation Economics"
              subtitle="System charge vs vendor cost (Video Studio)"
              height={320}
            >
              <Stack spacing={2} sx={{ height: "100%", justifyContent: "center" }}>
                {[
                  { label: "System charged", value: margin.system_charged, color: BRAND },
                  { label: "Vendor cost", value: margin.vendor_cost, color: AMBER },
                  { label: "Profit (credits)", value: margin.profit_credits, color: GREEN },
                ].map((row) => {
                  const max = Math.max(
                    Number(margin.system_charged || 0),
                    Number(margin.vendor_cost || 0),
                    1,
                  );
                  const pct = Math.min(100, (Number(row.value || 0) / max) * 100);
                  return (
                    <Box key={row.label}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {row.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={800} sx={{ color: row.color }}>
                          {fmtN(row.value)}
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          height: 10,
                          borderRadius: 99,
                          bgcolor: alpha(row.color, 0.12),
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${pct}%`,
                            height: "100%",
                            bgcolor: row.color,
                            borderRadius: 99,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
                <Divider />
                <Typography variant="caption" color="text.secondary">
                  Margin rate{" "}
                  <Box component="span" sx={{ fontWeight: 800, color: GREEN }}>
                    {fmtN(margin.margin_pct, 1)}%
                  </Box>
                  . Track this against provider pricing and task fees in Profit/Loss.
                </Typography>
              </Stack>
            </ChartCard>
          </Grid>
        </Grid>

        {/* Charts row 2 */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <ChartCard
              title="Revenue (30 days)"
              subtitle="Paid orders only — excludes free trials and $0 orders"
              height={260}
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenue} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GREEN} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={GREEN} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={gridColor} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={shortDate}
                    tick={{ fill: axisColor, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    tick={{ fill: axisColor, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={fmtCompact}
                    width={44}
                  />
                  <Tooltip
                    contentStyle={tip}
                    labelFormatter={(l) => moment(l).format("D MMM YYYY")}
                    formatter={(v, name) => [
                      name === "revenue" ? fmtMoney(v) : fmtN(v),
                      name === "revenue" ? "Revenue" : "Orders",
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke={GREEN}
                    fill="url(#revFill)"
                    strokeWidth={2.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <ChartCard title="User Growth (30 days)" subtitle="New registrations per day" height={260}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={growth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={gridColor} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={shortDate}
                    tick={{ fill: axisColor, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: axisColor, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                  />
                  <Tooltip
                    contentStyle={tip}
                    labelFormatter={(l) => moment(l).format("D MMM YYYY")}
                    formatter={(v) => [fmtN(v), "New users"]}
                  />
                  <Bar dataKey="count" name="Users" fill={BLUE} radius={[4, 4, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <ChartCard title="Plan Mix" subtitle="Paid vs free / trial accounts" height={260}>
              {planMix.length === 0 ? (
                <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                  <Typography variant="caption" color="text.disabled">
                    No user plan data
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={planMix}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={58}
                      outerRadius={88}
                      paddingAngle={3}
                    >
                      {planMix.map((entry, i) => (
                        <Cell key={entry.name} fill={i === 0 ? GREEN : SLATE} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tip} formatter={(v, n) => [fmtN(v), n]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
        </Grid>

        {/* Charts row 3 */}
        <Grid container spacing={2}>
          <Grid item xs={12} lg={5}>
            <ChartCard
              title="Credit Concentration by Task"
              subtitle="Where wallet balance is being consumed"
              height={300}
            >
              {taskPie.length === 0 ? (
                <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                  <Typography variant="caption" color="text.disabled">
                    No successful tasks yet
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={taskPie}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={52}
                      outerRadius={92}
                      paddingAngle={2}
                    >
                      {taskPie.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={tip}
                      formatter={(v, n, p) => [
                        `${fmtN(v)} cr · ${fmtN(p?.payload?.count)} jobs`,
                        n,
                      ]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} lg={7}>
            <ChartCard title="Top Tasks by Volume" subtitle="Jobs and credits consumed" height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={tasks.slice(0, 8).map((t) => ({
                    name: taskLabel(t.task),
                    count: Number(t.count || 0),
                    credits: Number(t.total_credits || 0),
                  }))}
                  layout="vertical"
                  margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                >
                  <CartesianGrid stroke={gridColor} strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fill: axisColor, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={130}
                    tick={{ fill: axisColor, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={tip}
                    formatter={(v, name) => [fmtN(v), name === "count" ? "Jobs" : "Credits"]}
                  />
                  <Legend />
                  <Bar dataKey="count" name="Jobs" fill={BLUE} radius={[0, 4, 4, 0]} maxBarSize={14} />
                  <Bar dataKey="credits" name="Credits" fill={BRAND} radius={[0, 4, 4, 0]} maxBarSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>

        {/* Ops reliability + tables */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <ChartCard title="Job Status Mix (7d)" subtitle="Reliability snapshot" height={240}>
              {statusPie.length === 0 ? (
                <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                  <Typography variant="caption" color="text.disabled">
                    No usage in the last 7 days
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusPie} dataKey="value" nameKey="name" outerRadius={80}>
                      {statusPie.map((s, i) => {
                        const name = String(s.name || "").toLowerCase();
                        const fill =
                          name === "success" || name === "completed"
                            ? GREEN
                            : name === "error" || name === "refunded" || name === "failed"
                              ? RED
                              : CHART_COLORS[i % CHART_COLORS.length];
                        return <Cell key={s.name} fill={fill} />;
                      })}
                    </Pie>
                    <Tooltip contentStyle={tip} formatter={(v, n) => [fmtN(v), n]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                height: "100%",
              }}
            >
              <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
                Recent Users
              </Typography>
              <Stack spacing={1.25}>
                {(data.recent_users || []).slice(0, 6).map((u) => {
                  let planTitle = null;
                  try {
                    planTitle = u.plan
                      ? (typeof u.plan === "string" ? JSON.parse(u.plan) : u.plan)?.title
                      : null;
                  } catch {
                    /* ignore */
                  }
                  return (
                    <Stack key={u.uid} direction="row" spacing={1.25} alignItems="center">
                      <Avatar
                        sx={{
                          width: 34,
                          height: 34,
                          bgcolor: alpha(BRAND, 0.15),
                          color: BRAND,
                          fontSize: 13,
                          fontWeight: 800,
                        }}
                      >
                        {(u.name || u.email || "U").charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {u.name || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" noWrap display="block">
                          {u.email}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end" spacing={0.25}>
                        <Chip
                          size="small"
                          label={u.status || "—"}
                          color={String(u.status).toLowerCase() === "active" ? "success" : "default"}
                          variant="outlined"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                        />
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                          {planTitle || "Free"}
                        </Typography>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                height: "100%",
              }}
            >
              <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
                Recent Activity
              </Typography>
              <Stack spacing={1.1}>
                {(data.recent_activity || []).slice(0, 7).map((log, i) => (
                  <Stack key={`${log.createdAt}-${i}`} direction="row" spacing={1} alignItems="flex-start">
                    {String(log.status).toLowerCase() === "success" ? (
                      <CheckCircleOutline sx={{ fontSize: 16, color: GREEN, mt: 0.3 }} />
                    ) : (
                      <ErrorOutline sx={{ fontSize: 16, color: RED, mt: 0.3 }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {taskLabel(log.task)}
                        </Typography>
                        <Typography variant="caption" fontWeight={800} color="warning.main">
                          {fmtN(log.credits)}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {log.user_name || "—"} · {log.user_email || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {moment(log.createdAt).fromNow()}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Dash;
